import React from 'react';
import { HowCalculated, DefinitionTable } from '../MetricsUI';

/*
 * Pieces shared by more than one admin tab.
 *
 * The single rule this file enforces: an organisation name is ALWAYS a link to
 * that organisation's manager view. It appears in a dozen places — tables,
 * rankings, hover lists, funnel rows — and it would be easy for one of them to
 * render plain text and quietly become a dead end.
 */

/**
 * An organisation name, wherever it appears. Always clickable.
 *
 * Carries an "Expired" tag when the org's subscription is over. Those orgs only
 * appear at all when the console's "include expired" switch is on, and every
 * tab names orgs through this, so the tag shows up everywhere they do.
 */
export function OrgLink({ org, onOpenOrg, className = '' }) {
  if (!org) return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpenOrg(org);
        }}
        title={`Open ${org.name}`}
        className={`text-left font-semibold text-navy hover:text-lavender hover:underline ${className}`}
      >
        {org.name}
      </button>
      {org.subscriptionExpired && <ExpiredTag />}
    </span>
  );
}

/** Marks an organisation whose subscription is over. */
export function ExpiredTag() {
  return (
    <span
      title="This organization's subscription has expired"
      className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600"
    >
      Expired
    </span>
  );
}

/** "2 years 3 months", from a month count. */
export const ageLabel = (months) => {
  if (months == null) return '—';
  if (months < 1) return 'this month';
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const y = Math.floor(months / 12);
  const m = months % 12;
  return `${y} year${y === 1 ? '' : 's'}${m ? ` ${m} month${m === 1 ? '' : 's'}` : ''}`;
};

export const fmtDay = (ms) =>
  ms == null ? '—' : new Date(ms).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });

/** 0-100 → a colour band. Used for health and for content difficulty. */
export const band = (v) => {
  if (v == null) return 'bg-gray-200';
  if (v < 40) return 'bg-rose-400';
  if (v < 55) return 'bg-amber-400';
  if (v < 70) return 'bg-yellow-300';
  return 'bg-emerald-500';
};

export function HealthPill({ health }) {
  if (!health || health.score == null) {
    return (
      <span
        className="rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-400 ring-1 ring-gray-200"
        title="Nothing measurable yet for this organisation"
      >
        —
      </span>
    );
  }
  const v = health.score;
  const tone =
    v >= 70
      ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
      : v >= 55
        ? 'bg-amber-50 text-amber-700 ring-amber-200'
        : 'bg-rose-50 text-rose-700 ring-rose-200';
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ring-1 ${tone}`}
      title={`Built from ${health.counted.join(', ')}`}
    >
      {v}
    </span>
  );
}

/**
 * The health explanation, wherever it is asked for.
 *
 * Takes the org so it can show the weights ACTUALLY used for that account —
 * the whole point of renormalising is undermined if the tooltip recites a fixed
 * 40/20/25/15 that did not apply.
 */
export function HealthHelp({ health, weights }) {
  const all = weights || [];
  const used = health ? health.effectiveWeights : [];
  const missing = health ? health.missing : [];

  return (
    <HowCalculated wide label="How health is calculated">
      <DefinitionTable
        title="Components"
        rows={[
          ['Adoption', 'Share of the roster active in the last 30 days, against a 90% target'],
          ['Recency', 'How long ago the typical member was last active — 0 at 40 days'],
          ['Improvement', 'Average per-person improvement, against a +10 point target'],
          ['Seat fill', 'Members with an account, against seats on record'],
        ]}
      />
      {used.length > 0 && (
        <span className="mb-2 block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Weights used for this organisation
          </span>
          <span className="block text-gray-600">
            {used.map((w) => `${w.label} ${w.weight}%`).join(' · ')}
          </span>
        </span>
      )}
      {missing.length > 0 && (
        <span className="block text-gray-500">
          Not counted here: <b>{missing.join(', ')}</b>. A component the platform never
          captured is dropped and the rest rescaled to 100% — scoring it zero would mark an
          account unhealthy for missing data rather than for missing engagement.
        </span>
      )}
      {all.length > 0 && used.length === all.length && (
        <span className="block text-gray-500">All four components had data for this organisation.</span>
      )}
    </HowCalculated>
  );
}

/** A labelled horizontal bar. Used for health components and content reach. */
export function MiniBar({ label, value, suffix = '', width = 'w-24', colour }) {
  return (
    <div className="flex items-center gap-2">
      <span className={`${width} shrink-0 text-xs text-gray-600`}>{label}</span>
      <div className="h-3 flex-1 overflow-hidden rounded bg-gray-100">
        <div
          className={`h-full rounded ${colour || band(value)}`}
          style={{ width: `${Math.max(0, Math.min(100, value || 0))}%` }}
        />
      </div>
      <span className="w-10 shrink-0 text-right text-xs tabular-nums text-navy">
        {value == null ? '—' : Math.round(value)}
        {suffix}
      </span>
    </div>
  );
}

/**
 * Canonical region ids -> the label people read.
 *
 * The server canonicalises to us / eu / ap; this is the only place that decides
 * what those look like on screen. Organization.regions holds four spellings of
 * three places ("US" on 19 orgs, "us" on 8, absent on 8), which is why the
 * column used to show US, us and — as if they were different states.
 */
export const REGION_LABEL = { us: 'US', eu: 'EU', ap: 'APAC' };

/**
 * Fold a region id to the one this client uses.
 *
 * A server may be running an older build than the page talking to it. One did:
 * production dbmaster answered with "apac" while this client matched on "ap",
 * so the APAC filter silently vanished — the region was present in the payload,
 * answering in 4 seconds with ok:true, and the page simply did not recognise
 * its name. Anything unrecognised is passed through rather than dropped, so a
 * new region shows up as itself instead of disappearing.
 */
export const REGION_ALIAS = { apac: 'ap', 'ap-southeast-1': 'ap', sg: 'ap', 'us-east-1': 'us', 'eu-west-2': 'eu' };
export const canonRegion = (r) => {
  const k = String(r || '').trim().toLowerCase();
  return REGION_ALIAS[k] || k;
};

/** A region list as a short string, or a dash when none is recorded. */
export const regionText = (regions, empty = '—') =>
  regions && regions.length ? regions.map((r) => REGION_LABEL[r] || r).join(', ') : empty;

/** The same list as chips, for a table cell that has room. */
export function RegionChips({ regions, empty = '—' }) {
  if (!regions || !regions.length) return <span className="text-gray-400">{empty}</span>;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {regions.map((r) => (
        <span key={r} className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-gray-600">
          {REGION_LABEL[r] || r}
        </span>
      ))}
    </span>
  );
}
